/**
 * The real mount point: covers the gap between the board and a child module mounting
 * (docs/concept/board/05-minigame-transition.md, 06-minimal-transition.png). `PlaySurface.tsx` renders this
 * in place of the child's `InstanceHost` whenever `view.child` exists on the immersive Momentum world; once
 * the local transition finishes it swaps to rendering the real child underneath, unmounting itself.
 *
 * Honest limitation (see the task's own note that this can't be exercised end to end today): there is no
 * server-side "module readiness" channel, so only the LOCAL participant's Ready press is tracked here — every
 * other participant starts (and stays) not-ready until such a channel exists. Since no shipped module
 * currently sets `launchesChildren: true` on the immersive world (`momentum-board-v2` sets it `false`), this
 * component cannot run today regardless; it is wired correctly for when a real mini-game module exists, and
 * is exercised for review via `BoardTransitionDemo.tsx` (`/dev/board-transition`) in the meantime.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { BoardView, type ModuleInstanceSummary, type ParticipantPublic, type SelfInfo } from '@momentum/shared';
import { BoardTransition } from './BoardTransition.tsx';
import { useBoardTransitionStage } from './useBoardTransitionStage.ts';
import { buildTransitionPlayers, buildTransitionTeams, describeTransitionFormat } from './boardTransitionData.ts';
import { strings } from '../shell/strings.ts';

export interface BoardTransitionGateProps {
  /** `session.child` (`ModuleInstanceSummary`) — carries participantIds/teams/deadlines. */
  child: ModuleInstanceSummary;
  /** `view.child`'s module name (the per-viewer `InstanceView` carries the same name, but not the teams). */
  childModuleName: string;
  participants: ParticipantPublic[];
  self: SelfInfo | null;
  serverTimeOffset: number;
  reducedMotion: boolean;
  /** The still-live root board, to render blurred behind the card — never a flat colour standing in for it. */
  rootBackdrop: ReactNode;
  /** The root instance's own projection, best-effort parsed for the persistent crystal HUD. */
  rootView?: unknown;
  /** The real child renderer, mounted once the transition completes. */
  children: ReactNode;
}

const CRYSTALS_TOTAL = 3; // Mirrors momentum-board.css's `momentum-crystal-count` convention (D22).

export function BoardTransitionGate({
  child,
  childModuleName,
  participants,
  self,
  serverTimeOffset,
  reducedMotion,
  rootBackdrop,
  rootView,
  children,
}: BoardTransitionGateProps) {
  const [selfReady, setSelfReady] = useState(false);
  const [done, setDone] = useState(false);

  // A new child instance is a new transition — reset if one somehow follows another.
  useEffect(() => {
    setSelfReady(false);
    setDone(false);
  }, [child.instanceId]);

  const players = buildTransitionPlayers(
    child.participantIds,
    child.teams,
    participants,
    new Set(selfReady && self ? [self.participantId] : []),
  );
  const readyCount = players.filter((p) => p.ready).length;

  const { stage, countdownValue } = useBoardTransitionStage({
    totalPlayers: players.length,
    readyCount,
    selfReady,
    onCountdownComplete: () => setDone(true),
  });

  if (done) return <>{children}</>;

  const disconnected = players.find((p) => p.disconnected);
  const board = BoardView.safeParse(rootView);
  const crystalsCollected = board.success ? Math.max(0, Math.min(CRYSTALS_TOTAL, board.data.progress.crystalsCollected ?? 0)) : 0;

  return (
    <BoardTransition
      stage={stage === 'starting' ? 'countdown' : stage}
      gameTitle={childModuleName}
      format={describeTransitionFormat(child.teams, child.participantIds.length, child.deadlines, Date.now() + serverTimeOffset)}
      players={players}
      teams={buildTransitionTeams(child.teams)}
      briefing={null}
      selfParticipantId={self?.participantId ?? null}
      countdownValue={countdownValue}
      onReady={() => setSelfReady(true)}
      readyDisabled={selfReady}
      disconnectNotice={disconnected ? strings.transition.disconnectNotice(disconnected.name) : null}
      hud={{ title: 'Momentum', crystalsCollected, crystalsTotal: CRYSTALS_TOTAL }}
      backdrop={rootBackdrop}
      reducedMotion={reducedMotion}
    />
  );
}
